/**
 * The cockpit's tooltip engine, ported from the POC (js/app.js installTooltips).
 *
 * Any element carrying `data-tip` shows a fixed-position tooltip on hover. Three reasons
 * this exists instead of the native `title` attribute, all of them the point of the tool:
 *  - it is INSTANT — the native ~1s delay makes a dense page feel like it explains nothing;
 *  - it is never clipped by a scroll container, so a definition on a row inside an
 *    overflowing table is still readable;
 *  - it can hold a full sentence legibly, which is what these definitions are.
 *
 * Mount once per page. Listening on the document (rather than per element) keeps this
 * O(1) no matter how many rows the page renders.
 */
import React, { useEffect, useRef } from 'react';

const WIDTH = 288;

export function TooltipHost() {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const tip = ref.current;
    if (!tip) return undefined;

    const hide = () => { tip.style.opacity = '0'; };

    const onOver = (e: Event) => {
      const target = (e.target as Element | null)?.closest?.('[data-tip]') as HTMLElement | null;
      if (!target) { hide(); return; }
      const text = target.dataset.tip;
      if (!text) { hide(); return; }
      tip.textContent = text;
      tip.style.opacity = '1';
      const r = target.getBoundingClientRect();
      // clamp horizontally so a tooltip on a screen-edge element stays fully visible
      tip.style.left = `${Math.max(8, Math.min(window.innerWidth - WIDTH - 8, r.left + r.width / 2 - WIDTH / 2))}px`;
      if (r.top > 96) {
        tip.style.top = '';
        tip.style.bottom = `${window.innerHeight - r.top + 8}px`;
      } else {
        tip.style.bottom = '';
        tip.style.top = `${r.bottom + 8}px`;
      }
    };

    document.addEventListener('mouseover', onOver);
    // capture: a tooltip anchored to a row must not linger over unrelated content
    document.addEventListener('scroll', hide, true);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('scroll', hide, true);
    };
  }, []);

  return <div className="pmc-tipfly" ref={ref} role="tooltip" aria-hidden="true" />;
}
